import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronRight, Share2, CheckSquare, Library, FileSymlink, Smartphone, X, ChevronLeft, Menu, Settings, Briefcase } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PromotionsUpdates } from './sidebar/PromotionsUpdates';
interface SidebarProps {
  onNavigate?: (path: string) => void;
  currentPath?: string;
}
export function Sidebar({
  onNavigate,
  currentPath: propCurrentPath
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = propCurrentPath || location.pathname;
  const [isDocumentDropdownOpen, setIsDocumentDropdownOpen] = useState(false);
  const [showMobilePromo, setShowMobilePromo] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  // Check if we're on a document hub page
  const isOnDocumentPage = currentPath === '/all-templates' || currentPath === '/my-documents' || currentPath === '/wills' || currentPath?.startsWith('/documents');
  const toggleDocumentDropdown = () => {
    setIsDocumentDropdownOpen(!isDocumentDropdownOpen);
  };
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    // Close dropdown when minimizing
    if (!isMinimized) {
      setIsDocumentDropdownOpen(false);
    }
  };
  const handleNavigation = (path: string) => {
    console.log(`Navigating to: ${path}`);
    // Map old paths to new paths
    let targetPath = path;
    if (path === '/documents/templates') targetPath = '/all-templates';
    if (path === '/documents/my-documents') targetPath = '/my-documents';
    if (path === '/documents/wills') targetPath = '/wills';
    if (onNavigate) {
      onNavigate(targetPath);
    } else {
      navigate(targetPath);
    }
  };
  return <div className={`${isMinimized ? 'w-20' : 'w-64'} bg-aliceBlue border-r border-borderColor flex flex-col h-full font-sans transition-all duration-300 relative`} data-id="element-883">
      {/* Logo Section */}
      <div className="p-4 bg-aliceBlue flex items-center" data-id="element-884">
        <div className={`flex items-center cursor-pointer overflow-hidden ${isMinimized ? 'w-0' : 'w-auto'} transition-all duration-300`} onClick={() => handleNavigation('/')} data-id="element-885">
          <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo Logo" className="h-10 w-auto" data-id="element-886" />
        </div>
      </div>

      {/* Toggle Button - Middle Right Edge */}
      <button onClick={toggleMinimize} className="absolute top-1/2 -translate-y-1/2 -right-3 z-50 p-1.5 rounded-full bg-white border border-borderColor shadow-md hover:shadow-lg transition-all text-secondary hover:text-primary" aria-label={isMinimized ? 'Expand sidebar' : 'Minimize sidebar'} data-id="element-887">
        {isMinimized ? <ChevronRight className="w-4 h-4" data-id="element-888" /> : <ChevronLeft className="w-4 h-4" data-id="element-889" />}
      </button>

      {/* Navigation — disabled in TELUS triage mode */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto pointer-events-none opacity-50 select-none" aria-disabled="true" data-id="element-890">
        <ul className="space-y-1" data-id="element-891">
          {/* Dashboard - Active State */}
          <li className="relative group" data-id="element-892">
            <a href="#" onClick={() => handleNavigation('/dashboard')} className={`flex items-center ${isMinimized ? 'justify-center px-3' : 'px-3'} py-2.5 ${currentPath === '/dashboard' || currentPath === '/' ? 'text-primary bg-white shadow-sm' : 'text-secondary hover:bg-white'} rounded-lg ${currentPath === '/dashboard' || currentPath === '/' ? isMinimized ? '' : 'border-l-4 border-violetBlue' : ''} cursor-pointer transition-all hover:shadow-md`} data-id="element-893">
              <img src="https://app.lawvo.com/static/media/Dashboard.324b7c31a1bc0f6c141b114aa75c7f11.svg" alt="Dashboard" className={`w-5 h-5 ${isMinimized ? '' : 'mr-3'}`} style={{
              filter: currentPath === '/dashboard' || currentPath === '/' ? 'brightness(0) saturate(100%) invert(28%) sepia(74%) saturate(2976%) hue-rotate(234deg) brightness(99%) contrast(101%)' : 'brightness(0) saturate(100%) invert(50%) sepia(8%) saturate(742%) hue-rotate(201deg) brightness(95%) contrast(88%)'
            }} data-id="element-894" />
              {!isMinimized && <span className={`text-sm ${currentPath === '/dashboard' || currentPath === '/' ? 'font-semibold' : 'font-medium'}`} data-id="element-895">
                  Dashboard
                </span>}
            </a>
            {isMinimized && <div className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 top-1/2 -translate-y-1/2" data-id="element-896">
                Dashboard
              </div>}
          </li>

          {/* LawVo AI */}
          <li className="relative group" data-id="element-897">
            <a href="#" onClick={() => handleNavigation('/ai-copilot')} className={`flex items-center ${isMinimized ? 'justify-center px-3' : 'px-3'} py-2.5 ${currentPath === '/ai-copilot' ? 'text-primary bg-white shadow-sm' : 'text-secondary hover:bg-white'} rounded-lg ${currentPath === '/ai-copilot' ? isMinimized ? '' : 'border-l-4 border-violetBlue' : ''} cursor-pointer transition-all hover:shadow-md`} data-id="element-898">
              <img src="https://cdn.magicpatterns.com/uploads/frDxN6VkzqMSZkD4nJP5uk/Screenshot_2025-12-31_at_11.06.59_AM.png" alt="LawVo AI" className={`w-5 h-5 ${isMinimized ? '' : 'mr-3'}`} data-id="element-899" />
              {!isMinimized && <span className={`text-sm ${currentPath === '/ai-copilot' ? 'font-semibold' : 'font-medium'}`} data-id="element-900">
                  LawVo AI
                </span>}
            </a>
            {isMinimized && <div className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 top-1/2 -translate-y-1/2" data-id="element-901">
                LawVo AI
              </div>}
          </li>

          {/* Document Hub - Dropdown */}
          <li className="relative group" data-id="element-902">
            <div data-id="element-903">
              <button onClick={toggleDocumentDropdown} disabled={isMinimized} className={`flex items-center w-full ${isMinimized ? 'justify-center px-3' : 'px-3'} py-2.5 ${isOnDocumentPage ? 'text-primary bg-white shadow-sm' : 'text-secondary hover:bg-white'} rounded-lg transition-colors cursor-pointer ${isOnDocumentPage && !isMinimized ? 'border-l-4 border-violetBlue' : ''}`} data-id="element-904">
                <img src="https://app.lawvo.com/static/media/document_hub_icon.c0c1fffeaa3071672047f0c09768c89f.svg" alt="Document Hub" className={`w-5 h-5 ${isMinimized ? '' : 'mr-3'} transition-opacity ${isOnDocumentPage ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} style={{
                filter: isOnDocumentPage ? 'brightness(0) saturate(100%) invert(28%) sepia(74%) saturate(2976%) hue-rotate(234deg) brightness(99%) contrast(101%)' : 'brightness(0) saturate(100%) invert(50%) sepia(8%) saturate(742%) hue-rotate(201deg) brightness(95%) contrast(88%)'
              }} data-id="element-905" />
                {!isMinimized && <>
                    <span className={`text-sm ${isOnDocumentPage ? 'font-semibold' : 'font-medium'}`} data-id="element-906">
                      Document Hub
                    </span>
                    {isDocumentDropdownOpen ? <ChevronDown className="w-4 h-4 ml-auto text-disabled" data-id="element-907" /> : <ChevronRight className="w-4 h-4 ml-auto text-disabled" data-id="element-908" />}
                  </>}
              </button>

              {!isMinimized && isDocumentDropdownOpen && <ul className="mt-1 space-y-0.5" data-id="element-909">
                  {[{
                icon: FileSymlink,
                label: 'My Templates',
                path: '/all-templates'
              }, {
                icon: FileText,
                label: 'My Documents',
                path: '/my-documents'
              }, {
                icon: Share2,
                label: 'Shared with Me',
                path: '/documents/shared'
              }, {
                icon: CheckSquare,
                label: 'Document Review',
                path: '/documents/review'
              }, {
                icon: Library,
                label: 'Document Library',
                path: '/documents/library'
              }, {
                icon: FileText,
                label: 'Wills',
                path: '/wills'
              }].map(item => <li key={item.label} data-id="element-910">
                      <a href="#" onClick={() => handleNavigation(item.path)} className={`flex items-center py-2 pl-11 pr-3 text-sm rounded-lg transition-colors cursor-pointer ${currentPath === item.path ? 'text-primary bg-white font-medium' : 'text-secondary hover:text-primary hover:bg-white'}`} data-id="element-911">
                        <item.icon className={`w-4 h-4 mr-2 ${currentPath === item.path ? 'text-violetBlue' : 'text-disabled'}`} data-id="element-912" />
                        <span className="font-[500]" data-id="element-913">{item.label}</span>
                      </a>
                    </li>)}
                </ul>}
            </div>
            {isMinimized && <div className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 top-1/2 -translate-y-1/2" data-id="element-914">
                Document Hub
              </div>}
          </li>

          {/* Claims */}
          <li className="relative group" data-id="element-915">
            <a href="#" onClick={() => handleNavigation('/claims')} className={`flex items-center ${isMinimized ? 'justify-center px-3' : 'px-3'} py-2.5 ${currentPath === '/claims' ? 'text-primary bg-white shadow-sm' : 'text-secondary hover:bg-white'} rounded-lg ${currentPath === '/claims' ? isMinimized ? '' : 'border-l-4 border-violetBlue' : ''} cursor-pointer transition-all hover:shadow-md`} data-id="element-916">
              <Briefcase className={`w-5 h-5 ${isMinimized ? '' : 'mr-3'}`} style={{
              color: currentPath === '/claims' ? '#7854FF' : '#7E8299'
            }} data-id="element-917" />
              {!isMinimized && <span className={`text-sm ${currentPath === '/claims' ? 'font-semibold' : 'font-medium'}`} data-id="element-918">
                  Claims
                </span>}
            </a>
            {isMinimized && <div className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 top-1/2 -translate-y-1/2" data-id="element-919">
                Claims
              </div>}
          </li>

          {/* Legal Health Check */}
          <li className="relative group" data-id="element-920">
            <a href="#" onClick={() => handleNavigation('/legal-health-check')} className={`flex items-center ${isMinimized ? 'justify-center px-3' : 'px-3'} py-2.5 ${currentPath === '/legal-health-check' ? 'text-primary bg-white shadow-sm' : 'text-secondary hover:bg-white'} rounded-lg ${currentPath === '/legal-health-check' ? isMinimized ? '' : 'border-l-4 border-violetBlue' : ''} cursor-pointer transition-all hover:shadow-md`} data-id="element-921">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={`w-5 h-5 ${isMinimized ? '' : 'mr-3'}`} style={{
              color: currentPath === '/legal-health-check' ? '#7854FF' : '#7E8299'
            }} data-id="element-922">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" data-id="element-923" />
              </svg>
              {!isMinimized && <span className={`text-sm ${currentPath === '/legal-health-check' ? 'font-semibold' : 'font-medium'}`} data-id="element-924">
                  Legal Health Check
                </span>}
            </a>
            {isMinimized && <div className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 top-1/2 -translate-y-1/2" data-id="element-925">
                Legal Health Check
              </div>}
          </li>

          {/* Other Nav Items */}
          {[{
          icon: 'https://app.lawvo.com/static/media/Msg.d8156aaa4702d83179ce7142b6464c60.svg',
          label: 'Inquiries',
          path: '/quotes'
        }, {
          icon: 'https://app.lawvo.com/static/media/Support.bc7ebfe1829ae914ee808c006a16b935.svg',
          label: '24/7 Legal Help',
          path: '/legal-help'
        }].map(item => <li key={item.label} className="relative group" data-id="element-926">
              <a href="#" onClick={() => handleNavigation(item.path)} className={`flex items-center ${isMinimized ? 'justify-center px-3' : 'px-3'} py-2.5 ${currentPath === item.path ? 'text-primary bg-white shadow-sm' : 'text-secondary hover:bg-white'} rounded-lg ${currentPath === item.path ? isMinimized ? '' : 'border-l-4 border-violetBlue' : ''} cursor-pointer transition-all hover:shadow-md`} data-id="element-927">
                <img src={item.icon} alt={item.label} className={`w-5 h-5 ${isMinimized ? '' : 'mr-3'} transition-opacity ${currentPath === item.path ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} style={{
              filter: currentPath === item.path ? 'brightness(0) saturate(100%) invert(28%) sepia(74%) saturate(2976%) hue-rotate(234deg) brightness(99%) contrast(101%)' : 'brightness(0) saturate(100%) invert(50%) sepia(8%) saturate(742%) hue-rotate(201deg) brightness(95%) contrast(88%)'
            }} data-id="element-928" />
                {!isMinimized && <span className={`text-sm ${currentPath === item.path ? 'font-semibold' : 'font-medium'}`} data-id="element-929">
                    {item.label}
                  </span>}
              </a>
              {isMinimized && <div className="absolute left-full ml-2 px-2 py-1 bg-primary text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 top-1/2 -translate-y-1/2" data-id="element-930">
                  {item.label}
                </div>}
            </li>)}
        </ul>
      </nav>

      {/* Bottom Section - Hidden when minimized */}
      {!isMinimized && <div className="p-4 space-y-4 bg-aliceBlue/50" data-id="element-931">
          {showMobilePromo && <div className="relative bg-white border border-borderColor rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-id="element-932">
              <button onClick={() => setShowMobilePromo(false)} className="absolute top-3 right-3 text-disabled hover:text-secondary p-1 rounded-md hover:bg-aliceBlue transition-colors cursor-pointer z-10" aria-label="Close" data-id="element-933">
                <X className="w-3.5 h-3.5" data-id="element-934" />
              </button>

              <div className="flex items-center gap-3 pr-6" data-id="element-935">
                <div className="bg-gradient-to-br from-aliceBlue to-borderColor p-2 rounded-lg border border-borderColor flex-shrink-0 shadow-sm" data-id="element-936">
                  <img src="https://api.qrserver.com/v1/create-qr-code/?size=96x96&data=https://lawvo.com/app" alt="Scan to download LawVo app" className="w-14 h-14" data-id="element-937" />
                </div>

                <div className="flex flex-col justify-center min-w-0 flex-1" data-id="element-938">
                  <h4 className="text-sm font-semibold text-primary leading-none mb-1" data-id="element-939">
                    LawVo Mobile App
                  </h4>
                  <p className="text-xs text-secondary leading-tight whitespace-nowrap" data-id="element-940">
                    Scan to download
                  </p>
                </div>
              </div>
            </div>}

          <PromotionsUpdates data-id="element-941" />
        </div>}
    </div>;
}